import { ModalInput } from "components/atoms/input/modal/ModalInput";
import { COLORS } from "styles/colors";
import styled from "styled-components";
import { calcRem } from "styles/theme";
import { FLEX } from "styles/flex";
import {
  ActivateKey,
  MergeableFreeKeys,
  SUBSCRIPTION_TYPE,
} from "api/interfaces/subscription.interface";
import { formatNumber, formatUppercaseOnly } from "utils/functions";
import {
  changeServiceNameUnit,
  useKeyType,
} from "hook/subscription/useKeyType";
import ConfirmActivateBottomInfo from "./ConfirmActivateBottomInfo";
import { useMemo } from "react";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { getAccountTimeFormatDate } from "utils/timeUtil";

type Props = {
  data: ActivateKey;
  userFixedStorage?: number;
  onChangeStorage: (number?: number | null) => void;
  mergeFreeKey?: string;
  onChangeMergeFreeKey: (dwKey?: string) => void;
};

export default function ConfirmActivation(props: Props): JSX.Element {
  const { data, mergeFreeKey, onChangeMergeFreeKey } = props;
  const { durationMonthOptions } = useKeyType();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const mergeableFreeKeys = useMemo(
    () =>
      [...(data.mergeableFreeKeys ?? [])].sort(
        (a, b) =>
          new Date(a.expireDate).getTime() - new Date(b.expireDate).getTime()
      ),
    [data.mergeableFreeKeys]
  );

  const selectedMergeFreeKey = useMemo(
    () => mergeableFreeKeys.find((freeKey) => freeKey.dwKey === mergeFreeKey),
    [mergeableFreeKeys, mergeFreeKey]
  );

  return (
    <Wrapper>
      <TopWrapper>
        <ModalInput label="Type" noBold>
          <h2>{formatUppercaseOnly(data.keyType)}</h2>
        </ModalInput>
        <ModalInput label={changeServiceNameUnit(data.keyType)} noBold>
          <h2>
            {formatNumber(
              data.keyType === SUBSCRIPTION_TYPE.STANDARD
                ? data.channels
                : data.capacity
            )}
          </h2>
        </ModalInput>
        <ModalInput label="Duration" noBold>
          <h2>
            {durationMonthOptions(data.keyType as SUBSCRIPTION_TYPE, true).find(
              (opt) => opt.value === String(data.duration)
            )
              ? durationMonthOptions(
                  data.keyType as SUBSCRIPTION_TYPE,
                  true
                ).find((opt) => opt.value === String(data.duration))?.label
              : ""}
          </h2>
        </ModalInput>
      </TopWrapper>

      {/* 
        1. COVA 장비만 있는 ORG 에서만.
        2. Register 된 Key 의 channel 수가 장비의 Channel 수 보다 많아야함
        3. 선택을 할 수도, 안 할 수도 있음 (미확정)
      */}
      {mergeableFreeKeys?.length > 0 && (
        <MergeFreeKeyWrapper>
          <MergeFreeKeyTitle>Merge Free Key</MergeFreeKeyTitle>
          <MergeFreeKeyList>
            {mergeableFreeKeys.map((freeKey: MergeableFreeKeys) => {
              const isChecked = mergeFreeKey === freeKey.dwKey;
              return (
                <MergeFreeKeyItem key={freeKey.dwKey}>
                  <input
                    type="radio"
                    name="mergeFreeKey"
                    checked={isChecked}
                    onChange={() => undefined}
                    onClick={() =>
                      onChangeMergeFreeKey(
                        isChecked ? undefined : freeKey.dwKey
                      )
                    }
                    data-testid={`subscription-modal-activate-merge-free-key-${freeKey.dwKey}`}
                  />
                  <span className="dw-key">{freeKey.dwKey}</span>
                  <span className="channel">
                    {formatNumber(freeKey.channel)} CH
                  </span>
                </MergeFreeKeyItem>
              );
            })}
          </MergeFreeKeyList>
        </MergeFreeKeyWrapper>
      )}
      {data.preActivated > 0 && (
        <ConfirmActivateBottomInfo
          {...props}
          userFixedStorage={props.userFixedStorage}
          selectedMergeFreeKey={selectedMergeFreeKey}
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  margin-top: 10px;
  color: ${({ theme }) => theme.COLORS.WHITE};

  h2 {
    font-weight: 500;
    font-size: ${calcRem(24)};
  }
`;

const TopWrapper = styled.div`
  width: 100%;
  ${FLEX.FlexBetweenStart};

  .common-modal-input {
    flex: 1;
  }
  .modal-input-label-title {
    color: ${COLORS.BUTTONS1};
  }
`;

const MergeFreeKeyWrapper = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.COLORS.PRIMARY3};
  padding: 20px 30px;
  margin-top: 16px;
  border-radius: 4px;
  color: ${({ theme }) => theme.COLORS.WHITE};
`;

const MergeFreeKeyTitle = styled.div`
  font-weight: 500;
  font-size: ${calcRem(16)};
  margin-bottom: 12px;
`;

const MergeFreeKeyList = styled.div`
  ${FLEX.FlexStartStart};
  flex-direction: column;
  gap: 10px;
  max-height: 160px;
  overflow-y: auto;
`;

const MergeFreeKeyItem = styled.label`
  ${FLEX.FlexStartCenter};
  width: 100%;
  gap: 20px;
  cursor: pointer;

  input[type="radio"] {
    cursor: pointer;
  }

  .channel {
    flex: 1;
  }
  .dw-key {
    flex: 2;
  }
  .expire-date {
    flex: 1;
    text-align: right;
  }
`;
